import { useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent } from 'react';

const subs = new Set<() => void>();
const emit = () => subs.forEach((s) => s());
// how many in-app pages are behind the current one (so «back» knows whether history leads inside the app)
let depth = 0;
window.addEventListener('popstate', () => { depth = Math.max(0, depth - 1); emit(); });

export function navigate(to: string, opts: { replace?: boolean } = {}) {
  if (to === location.pathname + location.search) return;
  if (opts.replace) history.replaceState(null, '', to);
  else { history.pushState(null, '', to); depth++; }
  emit();
  if (!opts.replace) window.scrollTo(0, 0);
}

/** previous in-app page when there is one, otherwise the overview */
export function goBack() {
  if (depth > 0) history.back();
  else navigate('/', { replace: true });
}

function subscribe(cb: () => void) {
  subs.add(cb);
  return () => { subs.delete(cb); };
}

export function useLocation() {
  const href = useSyncExternalStore(subscribe, () => location.pathname + location.search);
  const url = new URL(href, location.origin);
  return { path: url.pathname, query: url.searchParams };
}

/** update query params of the current URL (replace, no scroll) */
export function setQuery(patch: Record<string, string | number | null | undefined>) {
  const p = new URLSearchParams(location.search);
  for (const [k, v] of Object.entries(patch)) {
    if (v === null || v === undefined || v === '') p.delete(k);
    else p.set(k, String(v));
  }
  const s = p.toString();
  navigate(location.pathname + (s ? `?${s}` : ''), { replace: true });
}

export function Link({ to, onClick, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { to: string }) {
  const handle = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(to);
  };
  return <a href={to} onClick={handle} {...rest} />;
}
